import React, { useCallback, useEffect, useState } from 'react';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { Box, Button, Grid } from '@mui/material';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { EntityField, Operator, SelectedEntityFields } from '../../../../models/query-builder';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import Filter from './filter';
import SaveQueryModal from './save-query-modal';
import * as Yup from 'yup';
import FiltersContainer from './filter.container';
import QueryGridPage from '../../query-grid/query-grid.page';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useSaveData } from '../../../../hooks/useSaveData';
import { queryBuilderApiKeyStore } from '../../../../api/query-builder-api-key-store';
import { useParams } from 'react-router-dom';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { Output } from '@mui/icons-material';
import { FIELDS_TO_STORE_DEPENDENCIES } from './constants';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { FormAutoCompleteMultiSelectDynamic } from '../../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { FormAutoCompleteSelectDynamic } from '../../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { workorderReportApiKeyStore } from '../../../../api/workorder-report-api-key-store';
import FormDateField, { DateFormat } from '../../../../components/hook-form/form-date.field.component';
import { useAuthorize } from '../../../../hooks/use-authorize';

interface IFiltersPage {
  fields: EntityField[];
  operators: Operator[];
  handleColumnOptionsModalSubmit: (selectedFields: Record<number, EntityField>) => void;
  selectedEntityFields: Record<number, EntityField>;
}

interface FilterData {
  fieldId: number;
  operatorId: number;
  fieldValue: string;
  joinCondition?: string;
}

function generateRandomKey() {
  return Math.random().toString(36).substr(2, 32);
}

function transformFiltersWithOrder(input: any) {
  const result: {
    groups: Record<string, any>;
    groupKeys: string[];
    filterKeys: { [key: string]: string[] };
    dependencyIds: { [key: string]: { [key: string]: string } | null };
  } = {
    groups: {},
    groupKeys: [],
    filterKeys: {},
    dependencyIds: {},
  };

  input.forEach((group: any) => {
    const groupKey = nanoid(32);
    result.groupKeys.push(groupKey);
    const groupFilters: Record<string, any> = {};

    group.filters.forEach((filter: any) => {
      const filterKey = generateRandomKey();
      result.filterKeys[groupKey] = result.filterKeys[groupKey] ?? [];
      result.filterKeys[groupKey].push(filterKey);
      let fieldValue: any;
      let fieldIdValue: any;

      switch (filter.dataType) {
        case 'string':
          if (filter.operator === 'IN') {
            if (filter.controlType == 'MultiDropdown') {
              fieldValue = filter.columnValue.split(',').map((value: string) => ({
                value: value.trim().replace(/^'|'$/g, ''), // Remove single quotes
                label: value.trim().replace(/^'|'$/g, ''),
              }));
            } else {
              fieldValue = filter.columnValue.split(',').map((value: string) => value.trim().replace(/^'|'$/g, ''));
            }

            fieldIdValue = filter?.columnValueIds;

            if (FIELDS_TO_STORE_DEPENDENCIES.includes(filter.lookupColumn)) {
              result.dependencyIds[groupKey] = result.dependencyIds[groupKey] ?? {};
              result.dependencyIds[groupKey] = {
                ...result.dependencyIds[groupKey],
                [filter.lookupColumn]: filter?.columnValueIds,
              };
            }
          } else {
            if (filter.controlType == 'MultiDropdown') {
              fieldValue = filter.columnValue.split(',').map((value: string) => ({
                value: value.trim().replace(/^'|'$/g, ''), // Remove single quotes
                label: value.trim().replace(/^'|'$/g, ''),
              }));
            } else {
              fieldValue = filter.columnValue.split(',').map((value: string) => value.trim().replace(/^'|'$/g, ''));
            }
          }
          break;

        case 'date': {
          if (filter.columnValue.includes('##')) {
            const dateRange = filter.columnValue.split('##').map((date: string) => {
              const parsedDate = dayjs(date.trim());
              return parsedDate.isValid() ? parsedDate.toDate() : null;
            });
            fieldValue = dateRange.filter((date: string | null) => date !== null);
          } else if (filter.operator === '>' || filter.operator === '<') {
            const parsedDate = dayjs(filter.columnValue);
            fieldValue = parsedDate.isValid() ? parsedDate.toDate() : '';
          } else {
            const parsedDate = dayjs(filter.columnValue);

            fieldValue = parsedDate.isValid() ? parsedDate.format('MM/DD/YYYY') : filter.columnValue;
          }
          break;
        }

        default:
          fieldValue = filter.columnValue;
          fieldValue = 0;
      }

      // Add the filter to the group
      groupFilters[filterKey] = {
        fieldId: filter.entityFieldId,
        operatorId: filter.operatorId,
        fieldValue,
        fieldIdValue,
      };

      if (filter.condition) {
        groupFilters[filterKey].joinCondition = filter.condition;
      }
    });

    // Assign filters to the group
    result.groups[groupKey] = {
      filters: groupFilters,
    };

    result.groups[groupKey].joinCondition = !group.groupCondition ? '' : group.groupCondition;
  });

  return result;
}

const generateQBFiltersAndQBFieldsData = ({
  fields,
  operators,
  filtersFormData,
  selectedEntityFields,
  groupKeys,
}: {
  fields: EntityField[];
  operators: Operator[];
  filtersFormData: any;
  selectedEntityFields: Record<number, EntityField>;
  groupKeys: string[];
}) => {
  const qbFilterGroup = groupKeys.map((key) => ({
    groupCondition: filtersFormData.groups?.[key]?.joinCondition || '',
    filters: Object.values(filtersFormData.groups?.[key]?.filters).map((filter: any) => {
      const field = fields.find((field: any) => field.entityFieldId === filter.fieldId);
      const operator = operators.find((operator: any) => operator.operatorId === filter.operatorId);

      let columnValue;
      let columnIds;

      switch (true) {
        case field?.dataType === 'date' && (operator?.operatorCode === '=' || operator?.operatorCode === '!='):
          columnValue = filter.fieldValue; // Pass normal string like "this week"
          break;

        case field?.dataType === 'date' && operator?.operatorCode !== 'Between' && dayjs(filter.fieldValue).isValid():
          columnValue = dayjs(filter.fieldValue).format('MM/DD/YYYY');
          break;

        case field?.dataType === 'date' && operator?.operatorCode === 'Between':
          columnValue = filter.fieldValue
            .map(
              (date: any) =>
                dayjs(date).isValid()
                  ? dayjs(date).format('MM/DD/YYYY') // Format the valid date
                  : dayjs().format('MM/DD/YYYY') // Replace invalid date with the current date
            )
            .join('##'); // Join the formatted dates with '##'
          break;

        case dayjs(filter.fieldValue).isValid():
          columnValue = filter.fieldValue;
          break;

        case field?.controlType === 'MultiDropdown':
          columnValue = Array.isArray(filter.fieldValue)
            ? filter.fieldValue?.map((data: any) => data?.value)?.join('&')
            : '';
          columnIds = filter?.fieldIdValue;
          break;

        default:
          columnValue = filter.fieldValue;
          columnIds = '';
          break;
      }

      return {
        lookupColumn: field?.lookupColumn || '',
        columnValue: columnValue?.toString() || '',
        columnValueIds: columnIds?.toString() || '',
        operator: operator?.operatorCode || '',
        condition: filter.joinCondition || '',
        dataType: field?.dataType || '',
        operatorId: operator?.operatorId,
        controlType: field?.controlType,
        entityFieldId: field?.entityFieldId,
      };
    }),
  }));

  const qbFields = Object.values(selectedEntityFields).map((field) => ({
    entityFieldId: field.entityFieldId,
    fieldDisplayName: field.fieldDisplayName,
    fieldName: field.fieldName,
    dataType: field.dataType,
  }));

  return { qbFields, qbFilterGroup };
};

const FiltersPage = ({ fields, operators, selectedEntityFields, handleColumnOptionsModalSubmit }: IFiltersPage) => {
  const form = useReactHookForm({
    defaultValues: {},
    // yupSchema: filtersSchema,
  });

  const formForDateFields = useReactHookForm({
    defaultValues: {},
    // yupSchema: filtersSchema,
  });
  const { id } = useParams();
  const [groupKeys, setGroupKeys] = useState(!id ? [nanoid(32)] : []);

  const [showSaveQueryModal, setShowSaveQueryModal] = useState(false);
  const addNewGroupKey = () => setGroupKeys((prevKeys) => [...prevKeys, nanoid(32)]);

  const { data } = useLoadData<ApiResponse<any>>(queryBuilderApiKeyStore.getQuery(id));
  const [defaultGroupFilterKeys, setDefaultGroupFilterKeys] = useState<{ [key: string]: string[] }>();
  const [defaultDependencyIds, SetDefaultDependencyIds] = useState<{
    [key: string]: { [key: string]: string } | null;
  }>();

  useEffect(() => {
    if (data) {
      handleColumnOptionsModalSubmit(data?.data?.qbFields);
      const updateFormWithFilters = () => {
        if (data?.data?.qbFilterGroup) {
          const result = transformFiltersWithOrder(data.data.qbFilterGroup);
          form.setValue('groups', result.groups);
          setGroupKeys(result.groupKeys);
          setDefaultGroupFilterKeys(result.filterKeys);
          SetDefaultDependencyIds(result.dependencyIds);
        }
      };

      updateFormWithFilters();
    }
  }, [data, form]);

  const watchFields = form.watch();

  const handleDelete = useCallback(
    (filterKey: string) => {
      // Clearing last selected condition join

      const secondLastKey = groupKeys[groupKeys.length - 2];
      if (secondLastKey) {
        form.setValue(`filters.${[secondLastKey]}.joinCondition`, '');
      }

      setGroupKeys((prevKeys) => prevKeys.filter((key) => key !== filterKey));
    },
    [groupKeys]
  );

  const { isLoading, mutateAsync } = useSaveData<any>(queryBuilderApiKeyStore.saveQuery());
  const {
    isLoading: isLoadingRunQuery,
    mutateAsync: mutateAsyncRunQuery,
    data: runQueryData,
  } = useSaveData<any>(queryBuilderApiKeyStore.runQuery());

  const handleSubmit = async (saveQueryFormData: any) => {
    onRunQuery(saveQueryFormData);
    formForDateFields.setValue('fromDate', undefined);
    formForDateFields.setValue('toDate', undefined);
    formForDateFields.setValue('dateCriteria', []);
  };

  const handleSubmitFilter = async (saveQueryFormData: any) => {
    onRunQuery(saveQueryFormData);
  };

  const onRunQuery = (saveQueryFormData: any) => {
    const { qbFields, qbFilterGroup } = generateQBFiltersAndQBFieldsData({
      fields,
      operators,
      filtersFormData: watchFields as any,
      selectedEntityFields,
      groupKeys,
    });

    const dateCriterias = !saveQueryFormData?.dateCriteria
      ? []
      : saveQueryFormData?.dateCriteria?.map((item: any) => item?.dateCriteriaValue);

    const payload = {
      reportID: !id ? 0 : Number(id),

      reportName: 'test',
      isGlobal: false,
      qbFilterGroup: qbFilterGroup,
      qbFields: qbFields,

      isSave: true,
      dateCriterias: dateCriterias,
      dateFrom: !saveQueryFormData?.fromDate ? '' : saveQueryFormData?.fromDate,
      dateTo: !saveQueryFormData?.toDate ? '' : saveQueryFormData?.toDate,
    };

    const data = payload;
    mutateAsyncRunQuery({ data });
    // setShowSaveQueryModal(true);
  };

  const onSaveQuery = async () => {
    const isValid = await form.trigger();

    // If validation fails, stop execution
    if (!isValid) {
      // console.error('Form validation failed');
      return;
    }

    // Open the modal if validation passes
    if (!id) {
      setShowSaveQueryModal(true);
    } else {
      handleSaveQueryModalSubmit({});
    }
  };

  const handleSaveQueryModalSubmit = (saveQueryFormData: any) => {
    setShowSaveQueryModal(false);
    const { qbFields, qbFilterGroup } = generateQBFiltersAndQBFieldsData({
      fields,
      operators,
      filtersFormData: watchFields as any,
      selectedEntityFields,
      groupKeys,
    });

    const payload = {
      reportId: !id ? 0 : Number(id),

      reportName: !id ? saveQueryFormData?.reportName : data?.data?.reportName,
      isGlobal: !id ? saveQueryFormData?.isGlobal : data?.data?.isGlobal,
      qbFilterGroup: qbFilterGroup,
      qbFields: qbFields,

      IsSave: true,
    };
    mutateAsync({ data: payload });
  };

  const handleSaveQueryModalClose = () => setShowSaveQueryModal(false);

  const isMultiSelectReportEnabled = true;

  const handleDateCriteria = (value: any) => {
    if (value?.length > 0) {
      formForDateFields.setValue('fromDate', dayjs().subtract(2, 'week').format(DateFormat.SIMPLE_DATE));
      formForDateFields.setValue('toDate', dayjs().format(DateFormat.SIMPLE_DATE));
    } else {
      formForDateFields.setValue('fromDate', undefined);
      formForDateFields.setValue('toDate', undefined);
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <div>
        <ReactHookForm formName="QUWRY_BUILDER_FILTER_FORM" onSubmit={handleSubmit} formContextProps={form}>
          <Box
            style={{
              display: 'flex',
              marginTop: 30,
              marginBottom: 25,
              position: 'absolute',
              top: -92,
              right: 26,
            }}
          >
            <GuardedComponent requiredPermissions={[UserPermissionName.RUN_QUERY_CUSTOM_REPORT]}>
              <Button
                style={{
                  marginLeft: 'auto',
                }}
                type="submit"
                variant="contained"
                disabled={isLoadingRunQuery||isReadOnlyPermission}
                // onClick={isRunQueryLoading ? () => {} : () => onRunQuery()}
              >
                {isLoadingRunQuery ? 'Run Query' : 'Run Query'}
              </Button>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.SAVE_QUERY_CUSTOM_REPORT]}>
              <Button
                style={{
                  marginLeft: 15,
                }}
                variant="contained"
                type="button"
                disabled={isReadOnlyPermission}
                onClick={() => onSaveQuery()}
              >
                Save Query
              </Button>
            </GuardedComponent>
          </Box>
          <Box component="h3" marginTop={3} fontWeight={700} marginBottom={2}>
            Filters
          </Box>
          <Box
            className="flexGroup queryOuterWrap"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            {groupKeys.map((key, index) => (
              <FiltersContainer
                key={key}
                groupKey={key}
                fields={fields}
                operators={operators}
                prevGroupKey={groupKeys[index - 1]}
                addNewGroupKey={addNewGroupKey}
                groupKeysCount={groupKeys.length}
                handleDeleteGroup={handleDelete}
                index={index}
                defaultFilterKeys={defaultGroupFilterKeys?.[key]}
                defaultDependencyIds={defaultDependencyIds?.[key]}
              />
            ))}
          </Box>
        </ReactHookForm>
        {showSaveQueryModal && (
          <SaveQueryModal
            isVisible={showSaveQueryModal}
            onModalClose={handleSaveQueryModalClose}
            onSubmit={handleSaveQueryModalSubmit}
            //   isSaveQueryLoading={isSaveQueryLoading}
          />
        )}
      </div>
      {isLoadingRunQuery && <LoadingIndicator isLoading={isLoadingRunQuery} />}

      <Box>
        {(runQueryData || !!formForDateFields.watch('dateCriteria')) && (
          <ReactHookForm
            formName="QUERY_BUILDER_DATE_FORM"
            onSubmit={handleSubmitFilter}
            formContextProps={formForDateFields}
          >
            <Grid container spacing={1} style={{ padding: 12 }}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <Grid container spacing={1}>
                  <Grid item xl={3} lg={3} sm={4} xs={6}>
                    {isMultiSelectReportEnabled ? (
                      <FormAutoCompleteMultiSelectDynamic
                        name="dateCriteria"
                        label="Date Criteria"
                        onChange={handleDateCriteria}
                        getQueryKeyConfig={() => workorderReportApiKeyStore.getWorkOrderReportDateCriteria()}
                        optionsLabelKey="dateCriteria"
                        optionsValueKey="dateCriteriaValue"
                      />
                    ) : (
                      <FormAutoCompleteSelectDynamic
                        name="dateCriteria"
                        label="Date Criteria"
                        // onChange={handleStatusChange}
                        getQueryKeyConfig={() => workorderReportApiKeyStore.getWorkOrderReportDateCriteria()}
                        optionsValueKey="dateCriteriaValue"
                        optionsLabelKey="dateCriteria"
                      />
                    )}
                  </Grid>
                  <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
                    <FormDateField
                      label={'Date From'}
                      name="fromDate"
                      disabled={
                        !formForDateFields.watch('dateCriteria') ||
                        formForDateFields.watch('dateCriteria')?.length === 0
                      }
                      format={DateFormat.SIMPLE_DATE}
                      dateLimits={{
                        maxDateEnabled: true,
                        maxDateValue: 'toDate',
                      }}
                    />
                  </Grid>
                  <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
                    <FormDateField
                      label={'Date To'}
                      name="toDate"
                      disabled={
                        !formForDateFields.watch('dateCriteria') ||
                        formForDateFields.watch('dateCriteria')?.length === 0
                      }
                      format={DateFormat.SIMPLE_DATE}
                      dateLimits={{
                        minDateEnabled: true,
                        minDateValue: 'fromDate',
                      }}
                    />
                  </Grid>
                  <Grid item xl={4} lg={4} sm={4} xs={6}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                      <Button
                        disabled={
                          !formForDateFields.watch('dateCriteria') ||
                          formForDateFields.watch('dateCriteria')?.length === 0
                        }
                        type="submit"
                        title="Filter"
                        variant="contained"
                        startIcon={<FilterAltOutlinedIcon />}
                      >
                        Filter
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </ReactHookForm>
        )}
        {runQueryData && <QueryGridPage queryResult={runQueryData?.data} />}
      </Box>
    </>
  );
};

export default FiltersPage;
